import { supabase } from '@/lib/supabase';

/**
 * Attribut, mit dem Elemente vom Screenshot ausgenommen werden (der „Bug
 * melden"-Button selbst, der Dialog usw.). html2canvas überspringt sie.
 */
export const BUGREPORT_IGNORE_ATTR = 'data-bugreport-ignore' as const;

export class BugReportAuthRequiredError extends Error {
  constructor() {
    super('Anmeldung erforderlich');
    this.name = 'BugReportAuthRequiredError';
  }
}

export type CapturedScreenshot = {
  /** PNG-Data-URL des sichtbaren Viewports (Annotations-Basis). */
  dataUrl: string;
  width: number;
  height: number;
};

function shouldIgnore(node: unknown): boolean {
  return (
    node instanceof Element &&
    typeof node.hasAttribute === 'function' &&
    node.hasAttribute(BUGREPORT_IGNORE_ATTR)
  );
}

/**
 * html2canvas-Pfad: hält die korrekte Scroll- und Fixed-Sidebar-Lage (rechnet
 * den sichtbaren Ausschnitt explizit aus). Farben werden in JS nur angenähert
 * (Gradienten/Schatten/rgba-Tönungen können leicht abweichen).
 */
async function captureViaHtml2canvas(scale: number): Promise<CapturedScreenshot> {
  const { default: html2canvas } = await import('html2canvas');
  const width = window.innerWidth;
  const height = window.innerHeight;
  const canvas = await html2canvas(document.body, {
    backgroundColor: '#ffffff',
    logging: false,
    useCORS: true,
    scale,
    width,
    height,
    windowWidth: width,
    windowHeight: height,
    x: window.scrollX,
    y: window.scrollY,
    scrollX: 0,
    scrollY: 0,
    ignoreElements: (el) => shouldIgnore(el),
  });
  return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
}

/**
 * Findet die sichtbaren, fix-positionierten Overlays (z. B. die Sidebar). Nur
 * äußerste fixe Elemente (keine fixen Kinder eines fixen Elements), keine
 * vom Screenshot ausgenommenen. foreignObject löst fix/scroll nicht auf —
 * deshalb werden diese aus dem Content gefiltert und separat aufgeklebt.
 */
function findFixedOverlays(): HTMLElement[] {
  const result: HTMLElement[] = [];
  for (const el of Array.from(document.body.querySelectorAll<HTMLElement>('*'))) {
    if (shouldIgnore(el)) continue;
    const cs = getComputedStyle(el);
    if (cs.position !== 'fixed' || cs.visibility === 'hidden' || cs.display === 'none') continue;
    if (parseFloat(cs.opacity || '1') === 0) continue;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) continue;
    if (result.some((f) => f.contains(el))) continue; // nur äußerstes fixes Element
    result.push(el);
  }
  return result;
}

/**
 * modern-screenshot-Pfad: rendert das DOM via Browser-foreignObject → EXAKTE
 * Farben/Gradienten/Schatten. foreignObject kennt aber keinen Scroll-/Fixed-
 * Versatz, daher Composite: (1) Content OHNE fixe Overlays voll rendern und
 * auf den sichtbaren Viewport zuschneiden, (2) jedes fixe Overlay (Sidebar …)
 * separat rendern und an seiner Viewport-Position aufkleben. Empirisch
 * verifiziert für die fixe Sidebar bei beliebigem Scroll.
 */
async function captureViaModernScreenshot(scale: number): Promise<CapturedScreenshot> {
  const { domToCanvas } = await import('modern-screenshot');
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const sx = window.scrollX;
  const sy = window.scrollY;
  const fixedEls = findFixedOverlays();
  const isFixedOrIgnored = (node: unknown): boolean =>
    node instanceof Element && (shouldIgnore(node) || fixedEls.some((f) => f === node || f.contains(node)));

  // (1) Content ohne fixe Overlays, voll gerendert → auf Viewport zuschneiden.
  const contentFull = await domToCanvas(document.body, {
    backgroundColor: '#ffffff',
    scale,
    filter: (node) => !isFixedOrIgnored(node),
  });
  const cw = Math.round(vw * scale);
  const ch = Math.round(vh * scale);
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('kein 2d-Context');
  ctx.drawImage(contentFull, Math.round(sx * scale), Math.round(sy * scale), cw, ch, 0, 0, cw, ch);

  // (2) Fixe Overlays separat aufnehmen und an ihrer Viewport-Position aufkleben.
  for (const el of fixedEls) {
    try {
      const r = el.getBoundingClientRect();
      const overlay = await domToCanvas(el, {
        backgroundColor: null,
        scale,
        filter: (node) => !shouldIgnore(node),
      });
      ctx.drawImage(
        overlay,
        Math.round(r.left * scale),
        Math.round(r.top * scale),
        Math.round(r.width * scale),
        Math.round(r.height * scale)
      );
    } catch {
      /* einzelnes Overlay überspringen, Rest bleibt gültig */
    }
  }

  return { dataUrl: out.toDataURL('image/png'), width: out.width, height: out.height };
}

/**
 * Nimmt den aktuell sichtbaren Viewport auf. Aufnahme in Geräte-Pixeldichte
 * (max 2×) → auf Retina-Displays scharf; der finale Export skaliert auf
 * ≤1600px herunter.
 *
 * Standard: modern-screenshot (EXAKTE Farben via Browser-foreignObject) inkl.
 * Composite für fixe Overlays → korrekt bei beliebigem Scroll.
 * Ausnahme: sehr lange Seiten (Voll-Render würde Canvas-Limits sprengen) und
 * jeder Fehler → html2canvas-Fallback (rendert nur den Viewport, Farben aber
 * nur angenähert).
 */
export async function captureViewport(): Promise<CapturedScreenshot> {
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  // Voll-Render bei sehr langen Seiten vermeiden (Canvas-Größenlimit ~ Browser).
  const tooTallForFullRender = document.documentElement.scrollHeight * scale > 12000;
  if (!tooTallForFullRender) {
    try {
      return await captureViaModernScreenshot(scale);
    } catch (e) {
      console.warn('[bug-report] modern-screenshot fehlgeschlagen — Fallback html2canvas', e);
    }
  }
  return captureViaHtml2canvas(scale);
}

export type BugReportInput = {
  message: string;
  /** Finaler (ggf. annotierter) Screenshot als data:image/jpeg-URL. */
  screenshot?: string | null;
  annotated?: boolean;
  viewport?: { w: number; h: number } | null;
  contextPath?: string | null;
};

/**
 * Schickt einen Bug-Report an /api/bug-report-submit. Wirft
 * BugReportAuthRequiredError, wenn keine Session vorhanden ist.
 */
export async function submitBugReport(input: BugReportInput): Promise<{ ok: true; id?: string; screenshot_saved?: boolean }> {
  if (!supabase) throw new BugReportAuthRequiredError();
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token || token.startsWith('mock-local-dev')) throw new BugReportAuthRequiredError();

  const context_path =
    input.contextPath != null
      ? input.contextPath
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`.slice(0, 512)
        : null;

  const res = await fetch('/api/bug-report-submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      message: input.message,
      screenshot: input.screenshot ?? null,
      annotated: Boolean(input.annotated),
      viewport: input.viewport ?? null,
      context_path,
    }),
  });

  const json = (await res.json().catch(() => ({}))) as {
    error?: string;
    ok?: boolean;
    id?: string;
    screenshot_saved?: boolean;
  };
  if (!res.ok) {
    if (res.status === 401) throw new BugReportAuthRequiredError();
    throw new Error(json.error || `Bug-Report fehlgeschlagen (${res.status})`);
  }
  return { ok: true, id: json.id, screenshot_saved: json.screenshot_saved };
}
